import { Fragment, useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import {
  confirmCatalogImport,
  discardCatalogImportPreviewSession,
  getCatalogImportPreviewSession,
  importCatalogPreview,
  importCatalogPreviewRows,
  saveCatalogImportPreview,
  type CatalogImportTarget,
} from '../api/catalogImport'
import {
  CATALOG_IMPORT_FIELDS,
  catalogImportFieldLabel,
  columnMappingFromPreview,
  importMatchDecisionLabel,
  mappingSourceLabel,
  type CatalogImportConfirmResultDto,
  type CatalogImportPreviewDto,
  type CatalogImportPreviewRowsDto,
  type CatalogImportRowFieldOverride,
  type ColumnMapping,
  type ExistingItemUpdateMode,
  type ImportPreviewRowDto,
} from '../api/catalogImportTypes'
import { listProductTypes, mergeProductTypes, resolveProductTypes, type ProductTypeDto } from '../api/productTypes'
import { listSupplierCatalogFilterOptions } from '../api/supplierCatalog'
import { listRestaurantMyPricesFilterOptions, listRestaurantPrivateSuppliers, type RestaurantPrivateSupplierDto } from '../api/restaurantMyPrices'
import { useAuth } from '../auth/AuthProvider'
import { useActiveVenue } from '../venues/ActiveVenueProvider'
import { useTableColumnPrefs, type TableColumnDef } from '../hooks/useTableColumnPrefs'
import { innValidationMessage } from '../utils/inn'
import {
  ImportRowEditPanel,
  effectiveImportRowTypeId,
  buildConfirmProductTypeOverrides,
  importRowTypeLabel,
} from '../components/ImportRowEditPanel'
import { MarketEmptyState } from '../components/MarketEmptyState'
import { MarketLoadingIndicator } from '../components/MarketLoadingIndicator'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { ImportSupplierConfirmModal } from '../components/ImportSupplierConfirmModal'
import { ImportMatchingChangesModal } from '../components/ImportMatchingChangesModal'
import { ResizableTh } from '../components/ResizableTh'
import { SelectCombobox } from '../components/SelectCombobox'
import { TableColumnSettings } from '../components/TableColumnSettings'
import { IconPencil, IconTrash, IconUndo } from '../components/icons/CatalogActionIcons'
import { operatorBuyerPricesPath, operatorSupplierCatalogPath } from '../operator/paths'
import { formatApiErrorMessage } from '../api/apiErrorMessage'
import { billingErrorMessage, parseBillingError } from '../api/apiErrorCodes'
import { ApiError } from '../api/client'

import { PaywallModal } from '../components/billing/PaywallModal'
import {
  clearCatalogImportDraft,
  draftFromServerSession,
  formatDraftExpiry,
  loadCatalogImportDraft,
  saveCatalogImportDraft,
  type CatalogImportDraft,
} from '../storage/catalogImportDraft'
import {
  IMPORT_SESSION_EXPIRED_MESSAGE,
  isImportPreviewSessionApiError,
  isPreviewSessionExpired,
  previewSessionMinutesLeft,
} from '../utils/catalogImportSession'

// Keep pages small: preview rematches each row; large pages used to hit nginx 504 (org 18).
const PREVIEW_PAGE_SIZE = 25

const IMPORT_PREVIEW_COLUMNS: TableColumnDef[] = [
  { id: 'row', label: '#', required: true, pin: 'start', defaultWidth: 56, minWidth: 40 },
  { id: 'name', label: 'Наименование', required: true, defaultWidth: 220, minWidth: 120 },
  { id: 'price', label: 'Цена', defaultWidth: 88, minWidth: 64 },
  { id: 'unit', label: 'Ед. изм.', defaultWidth: 80, minWidth: 56 },
  { id: 'brand', label: 'Бренд', defaultWidth: 110, minWidth: 64 },
  { id: 'country', label: 'Страна', defaultWidth: 100, minWidth: 64 },
  { id: 'type', label: 'Тип', defaultWidth: 140, minWidth: 80 },
  { id: 'status', label: 'Тип определения', defaultWidth: 130, minWidth: 80 },
  {
    id: 'actions',
    label: 'Действия',
    required: true,
    pin: 'end',
    hideInSettings: true,
    defaultWidth: 96,
    minWidth: 80,
  },
]

const applyPreviewRowsExpiry = (
  preview: CatalogImportPreviewDto,
  expiresAt: string | null,
): CatalogImportPreviewDto =>
  expiresAt ? { ...preview, expiresAt } : preview

const columnSamplePreview = (values: string[]): string => {
  const trimmed = values.filter((value) => value.trim()).slice(0, 3)
  return trimmed.length > 0 ? trimmed.join(', ') : 'нет примеров'
}

const columnMappingsEqual = (left: ColumnMapping, right: ColumnMapping): boolean =>
  CATALOG_IMPORT_FIELDS.every((field) => (left[field] ?? null) === (right[field] ?? null))

const applyDraft = (draft: CatalogImportDraft) => ({
  preview: draft.preview,
  columnMapping: draft.columnMapping,
  visibleRows: draft.visibleRows,
  previewPage: draft.previewPage ?? Math.floor((draft.offset ?? 0) / PREVIEW_PAGE_SIZE),
  rowTypeOverrides: draft.rowTypeOverrides,
  rowFieldOverrides: draft.rowFieldOverrides,
  excludedRowNumbers: new Set(draft.excludedRowNumbers),
  matchingChangesFirst: draft.matchingChangesFirst ?? false,
  matchingChangeRowNumbers: draft.matchingChangeRowNumbers ?? [],
  privateSupplierId: draft.privateSupplierId ?? null,
  offset: draft.offset ?? (draft.previewPage ?? 0) * PREVIEW_PAGE_SIZE,
})

const ReplaceFileButton = ({
  onReplaceFile,
  replaceDisabled = false,
}: {
  onReplaceFile: () => void
  replaceDisabled?: boolean
}) => (
  <button
    type="button"
    className="echat-btn echat-btn--ghost echat-btn--sm"
    disabled={replaceDisabled}
    onClick={onReplaceFile}
  >
    Заменить файл
  </button>
)

const ImportAnalysisPanel = ({
  preview,
  onReplaceFile,
  replaceDisabled = false,
  showReplaceButton = true,
}: {
  preview: CatalogImportPreviewDto
  onReplaceFile: () => void
  replaceDisabled?: boolean
  showReplaceButton?: boolean
}) => {
  const minutesLeft = previewSessionMinutesLeft(preview)

  return (
    <article className="echat-panel market-import-analysis">
      <h2 className="echat-panel__subtitle">{preview.filename}</h2>
      <p className="echat-muted">
        {preview.totalRows} строк · {preview.columns.length} столбцов
        {preview.savedProfileApplied ? ' · применён сохранённый профиль' : ''}
      </p>
      {preview.expiresAt ? (
        <p className="echat-muted">Черновик на сервере до {formatDraftExpiry(preview.expiresAt)}</p>
      ) : null}
      {minutesLeft != null && minutesLeft <= 60 ? (
        <p className="echat-error" role="status">
          {minutesLeft <= 0
            ? 'Сессия импорта истекла — загрузите файл заново.'
            : `Сессия импорта истечёт через ${minutesLeft} мин. Завершите импорт или загрузите прайс снова.`}
        </p>
      ) : null}
      {showReplaceButton ? (
        <div className="market-import-analysis__actions">
          <ReplaceFileButton onReplaceFile={onReplaceFile} replaceDisabled={replaceDisabled} />
        </div>
      ) : null}
    </article>
  )
}

export const SupplierCatalogImportScreen = () => {
  const { session } = useAuth()
  const { activeVenueId } = useActiveVenue()
  const navigate = useNavigate()
  const location = useLocation()
  const { organizationId: organizationIdParam } = useParams()
  const routeOrganizationId = organizationIdParam ? Number(organizationIdParam) : null
  const hasRouteOrg =
    routeOrganizationId != null && Number.isFinite(routeOrganizationId) && routeOrganizationId > 0
  // «Мои прайсы»: и покупатель (/my-prices/import), и оператор (/operator/buyers/:id/prices/import).
  const isRestaurantMyPricesImport =
    location.pathname.includes('/my-prices/import') || location.pathname.endsWith('/prices/import')
  const importTarget: CatalogImportTarget = isRestaurantMyPricesImport ? 'restaurant-my-prices' : 'supplier'
  const columnPrefs = useTableColumnPrefs(
    isRestaurantMyPricesImport ? 'buyer:import-preview' : 'supplier:import-preview',
    IMPORT_PREVIEW_COLUMNS,
  )
  const organizationId =
    hasRouteOrg && routeOrganizationId != null ? routeOrganizationId : (session?.organizationId ?? 0)
  const catalogPath = isRestaurantMyPricesImport
    ? hasRouteOrg && routeOrganizationId != null
      ? operatorBuyerPricesPath(routeOrganizationId)
      : '/my-prices'
    : hasRouteOrg && routeOrganizationId != null
      ? operatorSupplierCatalogPath(routeOrganizationId)
      : '/catalog'
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const mappingPanelRef = useRef<HTMLElement | null>(null)
  const previewTableRef = useRef<HTMLDivElement | null>(null)
  const skipDraftOfferRef = useRef(Boolean((location.state as { preview?: CatalogImportPreviewDto } | null)?.preview))

  const initialPreview = (location.state as { preview?: CatalogImportPreviewDto } | null)?.preview
  const initialMapping = initialPreview ? columnMappingFromPreview(initialPreview) : {}

  const [preview, setPreview] = useState<CatalogImportPreviewDto | null>(initialPreview ?? null)
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>(() => initialMapping)
  /** Mapping last applied to the visible match-preview rows. */
  const [appliedColumnMapping, setAppliedColumnMapping] = useState<ColumnMapping>(() => initialMapping)
  const [visibleRows, setVisibleRows] = useState<ImportPreviewRowDto[]>(
    () => initialPreview?.sampleRows ?? [],
  )
  const [knownPreviewRows, setKnownPreviewRows] = useState<Record<number, ImportPreviewRowDto>>(
    () => Object.fromEntries((initialPreview?.sampleRows ?? []).map((row) => [row.rowNumber, row])),
  )
  const [previewPage, setPreviewPage] = useState(0)
  const [rowTypeOverrides, setRowTypeOverrides] = useState<Record<number, number>>({})
  const [rowFieldOverrides, setRowFieldOverrides] = useState<
    Record<number, CatalogImportRowFieldOverride>
  >({})
  const [excludedRowNumbers, setExcludedRowNumbers] = useState<Set<number>>(new Set())
  const [expandedRow, setExpandedRow] = useState<number | null>(null)
  const [productTypes, setProductTypes] = useState<ProductTypeDto[]>([])
  const [brandOptions, setBrandOptions] = useState<string[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [pendingDraft, setPendingDraft] = useState<CatalogImportDraft | null>(null)
  const [sessionLookupDone, setSessionLookupDone] = useState(
    () => Boolean(initialPreview) || skipDraftOfferRef.current,
  )
  const [savingDraft, setSavingDraft] = useState(false)
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null)
  const lastServerSaveKeyRef = useRef('')
  const [result, setResult] = useState<CatalogImportConfirmResultDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState<string | null>(null)
  const [pageLoading, setPageLoading] = useState(false)
  const [previewStale, setPreviewStale] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [billingPaywall, setBillingPaywall] = useState<{ title: string; message: string; planName: string } | null>(
    null,
  )
  const [privateSuppliers, setPrivateSuppliers] = useState<RestaurantPrivateSupplierDto[]>([])
  const [supplierMode, setSupplierMode] = useState<'existing' | 'new'>('new')
  const [selectedPrivateSupplierId, setSelectedPrivateSupplierId] = useState<number | null>(null)
  const [newSupplierLegalName, setNewSupplierLegalName] = useState('')
  const [newSupplierInn, setNewSupplierInn] = useState('')
  const [newSupplierDisplayLabel, setNewSupplierDisplayLabel] = useState('')
  const [supplierConfirmAction, setSupplierConfirmAction] = useState<'pick-file' | 'confirm-import' | null>(
    null,
  )
  const [matchingChangeCount, setMatchingChangeCount] = useState(
    () => initialPreview?.matchingChangeCount ?? 0,
  )
  const [matchingChangeCountComplete, setMatchingChangeCountComplete] = useState(
    () => initialPreview?.matchingChangeCountComplete ?? false,
  )
  const [highlightMatchingChanges, setHighlightMatchingChanges] = useState(false)
  const [matchingChangeRowNumbers, setMatchingChangeRowNumbers] = useState<number[]>([])
  const [existingItemUpdateMode, setExistingItemUpdateMode] = useState<ExistingItemUpdateMode | null>(
    null,
  )
  const [matchingChangesModalOpen, setMatchingChangesModalOpen] = useState(false)

  const typeNames = new Map(productTypes.map((type) => [type.id, type.name]))
  const previewTotalPages = Math.max(1, Math.ceil((preview?.totalRows ?? 0) / PREVIEW_PAGE_SIZE))
  const busy = loading || pageLoading

  const mergeKnownPreviewRows = useCallback((rows: ImportPreviewRowDto[]) => {
    setKnownPreviewRows((current) => {
      const next = { ...current }
      for (const row of rows) {
        next[row.rowNumber] = row
      }
      return next
    })
  }, [])

  const applyMatchingChangeMeta = useCallback((page: CatalogImportPreviewRowsDto | CatalogImportPreviewDto) => {
    if ('matchingChangeCountComplete' in page && page.matchingChangeCountComplete) {
      setMatchingChangeCount(page.matchingChangeCount)
      setMatchingChangeCountComplete(true)
      if ('matchingChangeRowNumbers' in page) {
        setMatchingChangeRowNumbers(page.matchingChangeRowNumbers)
      }
      return
    }
    if (!matchingChangeCountComplete) {
      setMatchingChangeCount(page.matchingChangeCount)
    }
  }, [matchingChangeCountComplete])

  const resetExpiredImportSession = useCallback(() => {
    if (organizationId > 0) {
      clearCatalogImportDraft(organizationId)
    }
    setPreview(null)
    setColumnMapping({})
    setAppliedColumnMapping({})
    setVisibleRows([])
    setKnownPreviewRows({})
    setPreviewPage(0)
    setRowTypeOverrides({})
    setRowFieldOverrides({})
    setExcludedRowNumbers(new Set())
    setPreviewStale(false)
    setPendingDraft(null)
    setSessionExpired(true)
    setMatchingChangeCount(0)
    setMatchingChangeCountComplete(false)
    setHighlightMatchingChanges(false)
    setMatchingChangeRowNumbers([])
    setExistingItemUpdateMode(null)
    setMatchingChangesModalOpen(false)
  }, [organizationId])

  const handleImportApiError = useCallback(
    (importError: unknown, fallback: string) => {
      if (isImportPreviewSessionApiError(importError)) {
        resetExpiredImportSession()
        setError(IMPORT_SESSION_EXPIRED_MESSAGE)
        return
      }
      const billingDetails = parseBillingError(importError)
      if (billingDetails) {
        setError(null)
        setBillingPaywall({
          title:
            billingDetails.errorCode === 'my_prices_refresh_cooldown'
              ? 'Обновление прайса'
              : 'Лимит «Мои прайсы»',
          message: billingErrorMessage(importError),
          planName: billingDetails.requiredPlanDisplayName ?? 'Стандарт',
        })
        return
      }
      setError(formatApiErrorMessage(importError, fallback))
    },
    [resetExpiredImportSession],
  )

  const proposedForField = (field: string) =>
    preview?.proposedMapping.find((mapping) => mapping.field === field)

  const scrollToColumnMapping = () => {
    mappingPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    mappingPanelRef.current?.classList.add('market-import-mapping-panel--focus')
    window.setTimeout(() => {
      mappingPanelRef.current?.classList.remove('market-import-mapping-panel--focus')
    }, 1600)
  }

  useEffect(() => {
    void listProductTypes().then(setProductTypes).catch(() => undefined)
  }, [])

  useEffect(() => {
    if (!visibleRows.length) {
      return
    }
    const knownIds = new Set(productTypes.map((type) => type.id))
    const referencedIds = visibleRows.flatMap((row) => {
      const effectiveId = effectiveImportRowTypeId(row, rowTypeOverrides)
      const candidateIds = row.matchPreview.candidates.map((candidate) => candidate.productTypeId)
      return effectiveId != null ? [effectiveId, ...candidateIds] : candidateIds
    })
    const missingIds = [
      ...new Set(referencedIds.filter((id): id is number => id != null && !knownIds.has(id))),
    ]
    if (missingIds.length === 0) {
      return
    }
    void resolveProductTypes(missingIds)
      .then((resolved) => {
        setProductTypes((current) => mergeProductTypes(current, resolved))
      })
      .catch(() => undefined)
  }, [visibleRows, rowTypeOverrides, productTypes])

  useEffect(() => {
    if (organizationId <= 0) {
      return
    }
    void (isRestaurantMyPricesImport
      ? listRestaurantMyPricesFilterOptions(organizationId)
      : listSupplierCatalogFilterOptions(organizationId))
      .then((options) => {
        setBrandOptions(options.brands)
        setCountryOptions(options.countries)
      })
      .catch(() => undefined)
  }, [organizationId, isRestaurantMyPricesImport])

  useEffect(() => {
    if (!isRestaurantMyPricesImport || organizationId <= 0) {
      return
    }
    void listRestaurantPrivateSuppliers(organizationId, activeVenueId)
      .then((suppliers) => {
        setPrivateSuppliers(suppliers)
        if (suppliers.length > 0) {
          setSupplierMode('existing')
          setSelectedPrivateSupplierId(suppliers[0]?.id ?? null)
        } else {
          setSupplierMode('new')
        }
      })
      .catch(() => undefined)
  }, [activeVenueId, organizationId, isRestaurantMyPricesImport])

  useEffect(() => {
    if (skipDraftOfferRef.current || initialPreview || organizationId <= 0) {
      setSessionLookupDone(true)
      return
    }
    let cancelled = false
    const restore = async () => {
      try {
        const session = await getCatalogImportPreviewSession(organizationId, importTarget)
        if (!cancelled) {
          setPendingDraft(draftFromServerSession(session, PREVIEW_PAGE_SIZE))
        }
      } catch (lookupError) {
        if (cancelled) {
          return
        }
        if (!(lookupError instanceof ApiError && lookupError.status === 404)) {
          const localDraft = loadCatalogImportDraft(organizationId)
          if (localDraft) {
            setPendingDraft(localDraft)
          }
          return
        }
        const draft = loadCatalogImportDraft(organizationId)
        if (draft) {
          setPendingDraft(draft)
        }
      } finally {
        if (!cancelled) {
          setSessionLookupDone(true)
        }
      }
    }
    void restore()
    return () => {
      cancelled = true
    }
  }, [initialPreview, organizationId, importTarget])

  useEffect(() => {
    if (!preview || result || organizationId <= 0) {
      return
    }
    const timer = window.setTimeout(() => {
      saveCatalogImportDraft(organizationId, {
        preview,
        columnMapping,
        rowTypeOverrides,
        rowFieldOverrides,
        excludedRowNumbers: [...excludedRowNumbers],
        visibleRows,
        offset: previewPage * PREVIEW_PAGE_SIZE,
        previewPage,
        matchingChangesFirst: highlightMatchingChanges,
        matchingChangeRowNumbers,
        privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
      })
    }, 400)
    return () => window.clearTimeout(timer)
  }, [
    preview,
    columnMapping,
    rowTypeOverrides,
    rowFieldOverrides,
    excludedRowNumbers,
    visibleRows,
    result,
    organizationId,
    previewPage,
    highlightMatchingChanges,
    matchingChangeRowNumbers,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
  ])

  useEffect(() => {
    if (!preview || result || organizationId <= 0 || sessionExpired) {
      return
    }
    const payload = {
      organizationId,
      previewToken: preview.previewToken,
      columnMapping,
      rowProductTypeOverrides: rowTypeOverrides,
      rowFieldOverrides,
      excludedRowNumbers: [...excludedRowNumbers],
      offset: previewPage * PREVIEW_PAGE_SIZE,
      matchingChangesFirst: highlightMatchingChanges,
      matchingChangeRowNumbers,
      privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
    }
    const saveKey = JSON.stringify(payload)
    if (saveKey === lastServerSaveKeyRef.current) {
      return
    }
    const timer = window.setTimeout(() => {
      void saveCatalogImportPreview(payload, importTarget)
        .then((saved) => {
          lastServerSaveKeyRef.current = saveKey
          setDraftSavedAt(saved.savedAt)
          setPreview((current) =>
            current ? applyPreviewRowsExpiry(current, saved.expiresAt) : current,
          )
        })
        .catch(() => undefined)
    }, 2500)
    return () => window.clearTimeout(timer)
  }, [
    preview,
    columnMapping,
    rowTypeOverrides,
    rowFieldOverrides,
    excludedRowNumbers,
    result,
    organizationId,
    previewPage,
    highlightMatchingChanges,
    matchingChangeRowNumbers,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
    sessionExpired,
    importTarget,
  ])

  useEffect(() => {
    if (!preview?.expiresAt || result) {
      return
    }
    const checkExpiry = () => {
      if (isPreviewSessionExpired(preview)) {
        resetExpiredImportSession()
        setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      }
    }
    checkExpiry()
    const timer = window.setInterval(checkExpiry, 30_000)
    return () => window.clearInterval(timer)
  }, [preview, result, resetExpiredImportSession])

  const handleResumeDraft = async () => {
    if (!pendingDraft || organizationId <= 0) {
      return
    }
    if (isPreviewSessionExpired(pendingDraft.preview)) {
      clearCatalogImportDraft(organizationId)
      setPendingDraft(null)
      setSessionExpired(true)
      setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      return
    }

    setLoading(true)
    setLoadingMessage('Проверяем сессию импорта…')
    setError(null)
    try {
      const next = applyDraft(pendingDraft)
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: pendingDraft.preview.previewToken,
          columnMapping: pendingDraft.columnMapping,
          offset: next.offset,
          limit: PREVIEW_PAGE_SIZE,
          ...(next.matchingChangesFirst
            ? {
                matchingChangesFirst: true,
                ...(next.matchingChangeRowNumbers.length > 0
                  ? { matchingChangeRowNumbers: next.matchingChangeRowNumbers }
                  : {}),
              }
            : {}),
          ...(isRestaurantMyPricesImport && next.privateSupplierId != null
            ? { privateSupplierId: next.privateSupplierId }
            : {}),
        },
        importTarget,
      )
      const restoredPreview = page.expiresAt
        ? applyPreviewRowsExpiry(next.preview, page.expiresAt)
        : next.preview
      setPreview({
        ...restoredPreview,
        totalRows: page.totalRows || restoredPreview.totalRows,
        sampleRows: page.rows,
      })
      setColumnMapping(next.columnMapping)
      setAppliedColumnMapping(next.columnMapping)
      setVisibleRows(page.rows.length > 0 ? page.rows : next.visibleRows)
      setKnownPreviewRows(
        Object.fromEntries((page.rows.length > 0 ? page.rows : next.visibleRows).map((row) => [row.rowNumber, row])),
      )
      setPreviewPage(Math.floor((page.offset ?? next.offset) / PREVIEW_PAGE_SIZE))
      setRowTypeOverrides(next.rowTypeOverrides)
      setRowFieldOverrides(next.rowFieldOverrides)
      setExcludedRowNumbers(next.excludedRowNumbers)
      setHighlightMatchingChanges(next.matchingChangesFirst)
      setMatchingChangeRowNumbers(
        page.matchingChangeRowNumbers.length > 0
          ? page.matchingChangeRowNumbers
          : next.matchingChangeRowNumbers,
      )
      if (next.privateSupplierId != null) {
        setSupplierMode('existing')
        setSelectedPrivateSupplierId(next.privateSupplierId)
      }
      applyMatchingChangeMeta(page)
      setPreviewStale(false)
      setPendingDraft(null)
      setResult(null)
      setSessionExpired(false)
      lastServerSaveKeyRef.current = ''
    } catch (resumeError) {
      handleImportApiError(resumeError, 'Не удалось восстановить черновик импорта')
      setPendingDraft(null)
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const handleDiscardDraft = () => {
    if (organizationId > 0) {
      clearCatalogImportDraft(organizationId)
      void discardCatalogImportPreviewSession(
        organizationId,
        pendingDraft?.preview.previewToken,
        importTarget,
      ).catch(() => undefined)
    }
    setPendingDraft(null)
  }

  const buildServerDraftPayload = () => {
    if (!preview) {
      return null
    }
    return {
      organizationId,
      previewToken: preview.previewToken,
      columnMapping,
      rowProductTypeOverrides: rowTypeOverrides,
      rowFieldOverrides,
      excludedRowNumbers: [...excludedRowNumbers],
      offset: previewPage * PREVIEW_PAGE_SIZE,
      matchingChangesFirst: highlightMatchingChanges,
      matchingChangeRowNumbers,
      privateSupplierId: isRestaurantMyPricesImport ? selectedPrivateSupplierId : null,
    }
  }

  const handleSaveDraft = async () => {
    const currentPreview = preview
    const payload = buildServerDraftPayload()
    if (!currentPreview || !payload || organizationId <= 0) {
      return
    }
    setSavingDraft(true)
    setError(null)
    try {
      const saved = await saveCatalogImportPreview(payload, importTarget)
      lastServerSaveKeyRef.current = JSON.stringify(payload)
      setDraftSavedAt(saved.savedAt)
      const nextPreview = applyPreviewRowsExpiry(currentPreview, saved.expiresAt)
      setPreview(nextPreview)
      saveCatalogImportDraft(organizationId, {
        preview: nextPreview,
        columnMapping,
        rowTypeOverrides,
        rowFieldOverrides,
        excludedRowNumbers: [...excludedRowNumbers],
        visibleRows,
        offset: payload.offset,
        previewPage,
        matchingChangesFirst: highlightMatchingChanges,
        matchingChangeRowNumbers,
        privateSupplierId: payload.privateSupplierId,
      })
    } catch (saveError) {
      handleImportApiError(saveError, 'Не удалось сохранить черновик импорта')
    } finally {
      setSavingDraft(false)
    }
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file || organizationId <= 0) {
      return
    }

    setLoading(true)
    setLoadingMessage('Распознаём прайс…')
    setError(null)
    setResult(null)
    try {
      const nextPreview = await importCatalogPreview(organizationId, file, importTarget)
      const mapping = columnMappingFromPreview(nextPreview)
      clearCatalogImportDraft(organizationId)
      setPreview(nextPreview)
      setColumnMapping(mapping)
      setAppliedColumnMapping(mapping)
      setVisibleRows(nextPreview.sampleRows)
      setKnownPreviewRows(
        Object.fromEntries(nextPreview.sampleRows.map((row) => [row.rowNumber, row])),
      )
      setPreviewPage(0)
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setExpandedRow(null)
      setPreviewStale(false)
      setPendingDraft(null)
      setSessionExpired(false)
      setMatchingChangeCount(nextPreview.matchingChangeCount)
      setMatchingChangeCountComplete(nextPreview.matchingChangeCountComplete)
      setHighlightMatchingChanges(false)
      setMatchingChangeRowNumbers([])
      setExistingItemUpdateMode(null)
      setMatchingChangesModalOpen(false)
    } catch (uploadError) {
      setError(formatApiErrorMessage(uploadError, 'Не удалось загрузить файл'))
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const refreshPreviewRows = useCallback(async () => {
    if (!preview || organizationId <= 0) {
      return
    }
    setLoading(true)
    setLoadingMessage('Обновляем предпросмотр…')
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: 0,
          limit: PREVIEW_PAGE_SIZE,
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
              sampleRows: page.rows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      setKnownPreviewRows(Object.fromEntries(page.rows.map((row) => [row.rowNumber, row])))
      setPreviewPage(0)
      setAppliedColumnMapping({ ...columnMapping })
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setExpandedRow(null)
      setPreviewStale(false)
      setHighlightMatchingChanges(false)
      setMatchingChangeRowNumbers([])
      applyMatchingChangeMeta(page)
    } catch (refreshError) {
      handleImportApiError(refreshError, 'Не удалось обновить предпросмотр')
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }, [
    columnMapping,
    preview,
    organizationId,
    handleImportApiError,
    importTarget,
    applyMatchingChangeMeta,
    isRestaurantMyPricesImport,
    selectedPrivateSupplierId,
  ])

  const handleMappingChange = (field: string, columnIndex: number | null) => {
    const next = { ...columnMapping, [field]: columnIndex }
    setColumnMapping(next)
    setPreviewStale(!columnMappingsEqual(next, appliedColumnMapping))
  }

  const handlePreviewPageChange = async (nextPage: number) => {
    if (!preview || organizationId <= 0 || pageLoading || loading || previewStale) {
      return
    }
    if (nextPage < 0 || nextPage >= previewTotalPages || nextPage === previewPage) {
      return
    }
    setPageLoading(true)
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: nextPage * PREVIEW_PAGE_SIZE,
          limit: PREVIEW_PAGE_SIZE,
          ...(highlightMatchingChanges
            ? {
                matchingChangesFirst: true,
                ...(matchingChangeRowNumbers.length > 0
                  ? { matchingChangeRowNumbers }
                  : {}),
              }
            : {}),
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      mergeKnownPreviewRows(page.rows)
      applyMatchingChangeMeta(page)
      setPreviewPage(nextPage)
      setExpandedRow(null)
    } catch (pageError) {
      handleImportApiError(pageError, 'Не удалось загрузить страницу предпросмотра')
    } finally {
      setPageLoading(false)
    }
  }

  const handleReviewMatchingChanges = async () => {
    setMatchingChangesModalOpen(false)
    setHighlightMatchingChanges(true)
    if (!preview || organizationId <= 0) {
      return
    }
    setLoading(true)
    setLoadingMessage('Показываем позиции с правками…')
    setError(null)
    try {
      const page = await importCatalogPreviewRows(
        {
          organizationId,
          previewToken: preview.previewToken,
          columnMapping,
          offset: 0,
          limit: PREVIEW_PAGE_SIZE,
          matchingChangesFirst: true,
          ...(matchingChangeRowNumbers.length > 0
            ? { matchingChangeRowNumbers }
            : {}),
          ...(isRestaurantMyPricesImport && selectedPrivateSupplierId != null
            ? { privateSupplierId: selectedPrivateSupplierId }
            : {}),
        },
        importTarget,
      )
      setPreview((current) =>
        current
          ? {
              ...applyPreviewRowsExpiry(current, page.expiresAt),
              totalRows: page.totalRows,
              sampleRows: page.rows,
            }
          : current,
      )
      setVisibleRows(page.rows)
      mergeKnownPreviewRows(page.rows)
      applyMatchingChangeMeta(page)
      setPreviewPage(0)
      setExpandedRow(null)
      if (typeof previewTableRef.current?.scrollIntoView === 'function') {
        previewTableRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    } catch (reviewError) {
      handleImportApiError(reviewError, 'Не удалось показать позиции с правками')
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const handleRowTypeOverride = (rowNumber: number, productTypeId: number | null) => {
    setRowTypeOverrides((current) => {
      const next = { ...current }
      if (productTypeId == null) {
        delete next[rowNumber]
      } else {
        next[rowNumber] = productTypeId
      }
      return next
    })
  }

  const handleRowFieldOverride = (
    rowNumber: number,
    override: CatalogImportRowFieldOverride | null,
  ) => {
    setRowFieldOverrides((current) => {
      const next = { ...current }
      if (override == null) {
        delete next[rowNumber]
      } else {
        next[rowNumber] = override
      }
      return next
    })
  }

  const handleToggleExcludedRow = (rowNumber: number) => {
    setExcludedRowNumbers((current) => {
      const next = new Set(current)
      if (next.has(rowNumber)) {
        next.delete(rowNumber)
      } else {
        next.add(rowNumber)
      }
      return next
    })
  }

  const resolveImportSupplierLabel = (): string => {
    if (supplierMode === 'new') {
      return newSupplierLegalName.trim() || newSupplierDisplayLabel.trim() || 'новый поставщик'
    }
    const selected = privateSuppliers.find((supplier) => supplier.id === selectedPrivateSupplierId)
    if (!selected) {
      return 'выбранный поставщик'
    }
    return selected.legalName || selected.displayLabel || `ИНН ${selected.inn}`
  }

  const validateMyPricesSupplierSelection = (): boolean => {
    if (!isRestaurantMyPricesImport) {
      return true
    }
    if (supplierMode === 'existing' && selectedPrivateSupplierId == null) {
      setError('Выберите профиль поставщика для импорта.')
      return false
    }
    if (supplierMode === 'new') {
      if (!newSupplierLegalName.trim()) {
        setError('Укажите юридическое наименование поставщика.')
        return false
      }
      const innError = innValidationMessage(newSupplierInn)
      if (innError) {
        setError(innError)
        return false
      }
    }
    return true
  }

  const openSupplierConfirm = (action: 'pick-file' | 'confirm-import') => {
    setError(null)
    if (!validateMyPricesSupplierSelection()) {
      return
    }
    setSupplierConfirmAction(action)
  }

  const handleConfirm = async (
    confirmedPrivateSupplierId?: number | null,
    updateMode?: ExistingItemUpdateMode | null,
  ) => {
    if (!preview || organizationId <= 0 || loading || pageLoading) {
      return
    }
    if (isPreviewSessionExpired(preview)) {
      resetExpiredImportSession()
      setError(IMPORT_SESSION_EXPIRED_MESSAGE)
      return
    }
    if (columnMapping.name == null) {
      setError('Укажите столбец для поля «Наименование».')
      return
    }
    if (isRestaurantMyPricesImport) {
      if (supplierMode === 'existing' && selectedPrivateSupplierId == null && confirmedPrivateSupplierId == null) {
        setError('Выберите профиль поставщика для импорта.')
        return
      }
      if (supplierMode === 'new' && confirmedPrivateSupplierId == null) {
        if (!newSupplierLegalName.trim()) {
          setError('Укажите юридическое наименование поставщика.')
          return
        }
        const innError = innValidationMessage(newSupplierInn)
        if (innError) {
          setError(innError)
          return
        }
      }
    }

    const effectivePrivateSupplierId =
      confirmedPrivateSupplierId != null ? confirmedPrivateSupplierId : selectedPrivateSupplierId
    const useExistingSupplier =
      isRestaurantMyPricesImport &&
      (confirmedPrivateSupplierId != null || supplierMode === 'existing')
    const mode = updateMode ?? existingItemUpdateMode

    let changeCount = matchingChangeCount
    if (!mode && !matchingChangeCountComplete) {
      setLoading(true)
      setLoadingMessage('Сверяем позиции с каталогом…')
      setError(null)
      try {
        const page = await importCatalogPreviewRows(
          {
            organizationId,
            previewToken: preview.previewToken,
            columnMapping,
            offset: 0,
            limit: PREVIEW_PAGE_SIZE,
            scanMatchingChanges: true,
            ...(useExistingSupplier && effectivePrivateSupplierId != null
              ? { privateSupplierId: effectivePrivateSupplierId }
              : {}),
          },
          importTarget,
        )
        applyMatchingChangeMeta(page)
        mergeKnownPreviewRows(page.rows)
        if (previewPage === 0) {
          setVisibleRows(page.rows)
        }
        setPreview((current) =>
          current ? { ...applyPreviewRowsExpiry(current, page.expiresAt), totalRows: page.totalRows } : current,
        )
        changeCount = page.matchingChangeCount
      } catch (scanError) {
        handleImportApiError(scanError, 'Не удалось сверить позиции с каталогом')
        return
      } finally {
        setLoading(false)
        setLoadingMessage(null)
      }
    }

    if (!mode && changeCount > 0) {
      setMatchingChangesModalOpen(true)
      return
    }

    setLoading(true)
    setLoadingMessage('Импортируем позиции…')
    setError(null)
    try {
      const confirmResult = await confirmCatalogImport(
        {
          organizationId,
          venueId: isRestaurantMyPricesImport ? activeVenueId : null,
          previewToken: preview.previewToken,
          columnMapping,
          rowProductTypeOverrides: buildConfirmProductTypeOverrides(
            Object.values(knownPreviewRows),
            rowTypeOverrides,
            excludedRowNumbers,
          ),
          rowFieldOverrides,
          excludedRowNumbers: [...excludedRowNumbers],
          ...(useExistingSupplier && effectivePrivateSupplierId != null
            ? { privateSupplierId: effectivePrivateSupplierId }
            : {}),
          ...(isRestaurantMyPricesImport && !useExistingSupplier && supplierMode === 'new'
            ? {
                newSupplier: {
                  legalName: newSupplierLegalName.trim(),
                  inn: newSupplierInn.trim(),
                  displayLabel: newSupplierDisplayLabel.trim() || null,
                },
              }
            : {}),
          ...(mode ? { existingItemUpdateMode: mode } : {}),
        },
        importTarget,
      )
      clearCatalogImportDraft(organizationId)
      setResult(confirmResult)
      setPreview(null)
      setVisibleRows([])
      setKnownPreviewRows({})
      setPreviewPage(0)
      setRowTypeOverrides({})
      setRowFieldOverrides({})
      setExcludedRowNumbers(new Set())
      setMatchingChangeCount(0)
      setMatchingChangeCountComplete(false)
      setHighlightMatchingChanges(false)
      setExistingItemUpdateMode(null)
      setMatchingChangesModalOpen(false)
    } catch (confirmError) {
      handleImportApiError(confirmError, 'Не удалось импортировать')
    } finally {
      setLoading(false)
      setLoadingMessage(null)
    }
  }

  const handleSupplierConfirmAccepted = (privateSupplierId: number | null) => {
    const action = supplierConfirmAction
    setSupplierConfirmAction(null)
    if (privateSupplierId != null) {
      setSupplierMode('existing')
      setSelectedPrivateSupplierId(privateSupplierId)
    }
    if (action === 'pick-file') {
      fileInputRef.current?.click()
      return
    }
    if (action === 'confirm-import') {
      void handleConfirm(privateSupplierId)
    }
  }

  if (organizationId <= 0) {
    return (
      <section className="market-page market-page--wide">
        <p className="echat-error">Не удалось определить организацию для импорта.</p>
      </section>
    )
  }

  return (
    <section className="market-page market-page--wide">
      <MarketPageHeader
        title={isRestaurantMyPricesImport ? 'Импорт в «Мои прайсы»' : 'Импорт прайса'}
        description="Файлы xlsx, xls или csv · сессия предпросмотра на сервере 72 ч"
        actions={
          <button type="button" className="echat-back" onClick={() => navigate(catalogPath)}>
            {isRestaurantMyPricesImport ? '← К моим прайсам' : '← К каталогу'}
          </button>
        }
      />

      <PaywallModal
        open={billingPaywall != null}
        title={billingPaywall?.title ?? ''}
        message={billingPaywall?.message ?? ''}
        onDismiss={() => setBillingPaywall(null)}
        onUpgrade={() => navigate('/payments')}
      />

      <input
        ref={fileInputRef}
        type="file"
        className="visually-hidden"
        accept=".xlsx,.xls,.csv"
        onChange={(event) => void handleFileChange(event)}
      />

      {pendingDraft ? (
        <article className="echat-panel echat-panel--highlight">
          <h2 className="echat-panel__subtitle">Продолжить импорт?</h2>
          <p className="echat-panel__text">
            Найден черновик «{pendingDraft.preview.filename}»
            {(pendingDraft.previewPage ?? 0) > 0
              ? ` · вернём на страницу ${(pendingDraft.previewPage ?? 0) + 1}`
              : ''}
            . Сессия на сервере до {formatDraftExpiry(pendingDraft.preview.expiresAt)}.
          </p>
          <div className="market-inline-actions">
            <button type="button" className="echat-btn echat-btn--primary" onClick={() => void handleResumeDraft()}>
              Продолжить
            </button>
            <button type="button" className="echat-btn echat-btn--ghost" onClick={handleDiscardDraft}>
              Начать заново
            </button>
          </div>
        </article>
      ) : null}

      {error ? (
        <p className="echat-error" role="alert">
          {error}
        </p>
      ) : null}
      {loading && loadingMessage && (preview || pendingDraft || result) ? (
        <MarketLoadingIndicator message={loadingMessage} />
      ) : null}

      {result ? (
        <article className="echat-panel">
          <h2 className="echat-panel__subtitle">Импорт завершён</h2>
          <p className="echat-panel__text">Импортировано: {result.imported}</p>
          <p className="echat-muted">
            Определены автоматически: {result.autoMatched} · Требуют внимания: {result.suggested}{' '}
            · Без типа:{' '}
            {result.unmatched} · Обновлено: {result.updatedExisting}
          </p>
          <div className="market-inline-actions">
            <button type="button" className="echat-btn echat-btn--primary" onClick={() => navigate(catalogPath)}>
              Открыть каталог
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              onClick={() => {
                setResult(null)
                fileInputRef.current?.click()
              }}
            >
              Импортировать другой файл
            </button>
          </div>
        </article>
      ) : null}

      {!result && !preview && !pendingDraft ? (
        !sessionLookupDone || loading ? (
          <article className="echat-panel">
            <MarketLoadingIndicator
              message={
                loadingMessage
                  ?? (!sessionLookupDone ? 'Ищем сохранённый импорт…' : 'Распознаём прайс…')
              }
            />
            <div className="market-import-preview-skeleton" aria-hidden="true">
              <div className="market-import-preview-skeleton__row" />
              <div className="market-import-preview-skeleton__row" />
              <div className="market-import-preview-skeleton__row" />
              <div className="market-import-preview-skeleton__row" />
            </div>
          </article>
        ) : isRestaurantMyPricesImport && privateSuppliers.length > 0 ? (
          <article className="echat-panel market-import-supplier">
            <header className="market-import-supplier__header">
              <h2 className="echat-panel__subtitle">Выберите поставщика</h2>
              <p className="market-muted market-import-supplier__lead">
                Укажите профиль поставщика, затем загрузите файл прайса.
              </p>
            </header>

            <div className="market-import-supplier__modes" role="radiogroup" aria-label="Режим поставщика">
              <label className="market-import-supplier__mode">
                <input
                  type="radio"
                  name="supplierModePick"
                  checked={supplierMode === 'existing'}
                  onChange={() => setSupplierMode('existing')}
                />
                <span>Существующий</span>
              </label>
              <label className="market-import-supplier__mode">
                <input
                  type="radio"
                  name="supplierModePick"
                  checked={supplierMode === 'new'}
                  onChange={() => setSupplierMode('new')}
                />
                <span>Новый</span>
              </label>
            </div>

            <div className="market-import-supplier__fields">
              {supplierMode === 'existing' ? (
                <SelectCombobox
                  className="market-import-supplier__profile"
                  label="Профиль"
                  value={selectedPrivateSupplierId != null ? String(selectedPrivateSupplierId) : ''}
                  options={privateSuppliers.map((supplier) => ({
                    value: String(supplier.id),
                    label: `${supplier.displayLabel || supplier.legalName} (ИНН ${supplier.inn})`,
                  }))}
                  onChange={(next) => setSelectedPrivateSupplierId(next ? Number(next) : null)}
                />
              ) : (
                <>
                  <label className="echat-field market-import-supplier__field">
                    <span className="echat-field__label">Юридическое наименование *</span>
                    <input
                      className="echat-field__input"
                      value={newSupplierLegalName}
                      onChange={(event) => setNewSupplierLegalName(event.target.value)}
                    />
                  </label>
                  <label className="echat-field market-import-supplier__field">
                    <span className="echat-field__label">ИНН *</span>
                    <input
                      className="echat-field__input"
                      value={newSupplierInn}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={14}
                      onChange={(event) => setNewSupplierInn(event.target.value)}
                    />
                  </label>
                  <label className="echat-field market-import-supplier__field market-import-supplier__field--wide">
                    <span className="echat-field__label">Отображаемое имя</span>
                    <input
                      className="echat-field__input"
                      value={newSupplierDisplayLabel}
                      placeholder="Как показывать в каталоге"
                      onChange={(event) => setNewSupplierDisplayLabel(event.target.value)}
                    />
                  </label>
                </>
              )}
            </div>

            <div className="market-inline-actions">
              {supplierMode === 'existing' ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  onClick={() => setSupplierMode('new')}
                >
                  Новый поставщик
                </button>
              ) : null}
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={supplierMode === 'existing' && selectedPrivateSupplierId == null}
                onClick={() => openSupplierConfirm('pick-file')}
              >
                Выбрать файл
              </button>
            </div>
          </article>
        ) : (
          <MarketEmptyState
            title={sessionExpired ? 'Сессия импорта истекла' : 'Выберите файл прайса'}
            description={
              sessionExpired
                ? 'Пока вы правили категории в каталоге, серверная сессия предпросмотра закончилась. Загрузите тот же прайс снова — правки в каталоге сохранены.'
                : 'Система распознает столбцы и предложит сопоставление с категориями Провизиум. Перед импортом проверьте маппинг и строки с неуверенным матчингом.'
            }
            actions={[
              {
                label: 'Выбрать файл',
                onClick: () => fileInputRef.current?.click(),
              },
            ]}
          />
        )
      ) : null}

      {preview ? (
        <>
          {preview.warnings.length > 0 ? (
            <article className="echat-panel echat-panel--highlight">
              <h2 className="echat-panel__subtitle">Обратите внимание</h2>
              <ul>
                {preview.warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
            </article>
          ) : null}

          {isRestaurantMyPricesImport ? (
            <div className="market-import-top-row">
              <article className="echat-panel market-import-supplier">
                <header className="market-import-supplier__header">
                  <h2 className="echat-panel__subtitle">Поставщик прайса</h2>
                  <p className="market-muted market-import-supplier__lead">
                    Укажите данные при первом импорте или выберите существующий профиль.
                  </p>
                </header>

                {privateSuppliers.length > 0 ? (
                  <div className="market-import-supplier__modes" role="radiogroup" aria-label="Режим поставщика">
                    <label className="market-import-supplier__mode">
                      <input
                        type="radio"
                        name="supplierMode"
                        checked={supplierMode === 'existing'}
                        onChange={() => setSupplierMode('existing')}
                      />
                      <span>Существующий</span>
                    </label>
                    <label className="market-import-supplier__mode">
                      <input
                        type="radio"
                        name="supplierMode"
                        checked={supplierMode === 'new'}
                        onChange={() => setSupplierMode('new')}
                      />
                      <span>Новый</span>
                    </label>
                  </div>
                ) : null}

                <div className="market-import-supplier__fields">
                  {supplierMode === 'existing' && privateSuppliers.length > 0 ? (
                    <div className="market-import-supplier__profile-row">
                      <SelectCombobox
                        className="market-import-supplier__profile"
                        label="Профиль"
                        value={selectedPrivateSupplierId != null ? String(selectedPrivateSupplierId) : ''}
                        options={privateSuppliers.map((supplier) => ({
                          value: String(supplier.id),
                          label: `${supplier.displayLabel || supplier.legalName} (ИНН ${supplier.inn})`,
                        }))}
                        onChange={(next) =>
                          setSelectedPrivateSupplierId(next ? Number(next) : null)
                        }
                      />
                      <div className="market-import-supplier__replace">
                        <ReplaceFileButton
                          onReplaceFile={() => fileInputRef.current?.click()}
                          replaceDisabled={busy}
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <label className="echat-field market-import-supplier__field">
                        <span className="echat-field__label">Юридическое наименование *</span>
                        <input
                          className="echat-field__input"
                          value={newSupplierLegalName}
                          onChange={(event) => setNewSupplierLegalName(event.target.value)}
                        />
                      </label>
                      <label className="echat-field market-import-supplier__field">
                        <span className="echat-field__label">ИНН *</span>
                        <input
                          className="echat-field__input"
                          value={newSupplierInn}
                          inputMode="numeric"
                          autoComplete="off"
                          maxLength={14}
                          onChange={(event) => setNewSupplierInn(event.target.value)}
                        />
                      </label>
                      <label className="echat-field market-import-supplier__field market-import-supplier__field--wide">
                        <span className="echat-field__label">Отображаемое имя</span>
                        <input
                          className="echat-field__input"
                          value={newSupplierDisplayLabel}
                          placeholder="Как показывать в каталоге"
                          onChange={(event) => setNewSupplierDisplayLabel(event.target.value)}
                        />
                      </label>
                    </>
                  )}
                </div>
              </article>

              <ImportAnalysisPanel
                preview={preview}
                onReplaceFile={() => fileInputRef.current?.click()}
                replaceDisabled={busy}
                showReplaceButton={!(supplierMode === 'existing' && privateSuppliers.length > 0)}
              />
            </div>
          ) : (
            <ImportAnalysisPanel
              preview={preview}
              onReplaceFile={() => fileInputRef.current?.click()}
              replaceDisabled={busy}
            />
          )}

          <article
            ref={mappingPanelRef}
            id="import-column-mapping"
            className="echat-panel market-import-mapping-panel"
          >
            <div className="market-import-section-header">
              <h2 className="echat-panel__subtitle">Сопоставление столбцов</h2>
              {previewStale ? (
                <button
                  type="button"
                  className="echat-btn echat-btn--primary echat-btn--sm"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              ) : null}
            </div>
            <p className="echat-muted market-import-section-lead">
              Если автоопределение ошиблось (цена в «Стране», описание в «Бренде»), поправьте поля
              ниже и обновите предпросмотр.
            </p>
            <div className="market-import-mapping">
              {CATALOG_IMPORT_FIELDS.map((field) => {
                const proposed = proposedForField(field)
                return (
                  <div key={field} className="echat-field market-import-mapping__row">
                    <SelectCombobox
                      label={catalogImportFieldLabel(field)}
                      value={columnMapping[field] != null ? String(columnMapping[field]) : ''}
                      options={[
                        { value: '', label: '— не использовать —' },
                        ...preview.columns.map((column) => ({
                          value: String(column.index),
                          label: `${column.header || `Столбец ${column.index + 1}`} (${columnSamplePreview(column.sampleValues)})`,
                        })),
                      ]}
                      onChange={(next) =>
                        handleMappingChange(field, next ? Number(next) : null)
                      }
                    />
                    {proposed?.source ? (
                      <span className="echat-muted market-import-mapping__hint">
                        {mappingSourceLabel(proposed.source)}
                      </span>
                    ) : null}
                  </div>
                )
              })}
            </div>

            {previewStale ? (
              <div className="market-inline-actions market-import-stale-actions">
                <p className="echat-muted">Маппинг изменился — обновите предпросмотр матчинга.</p>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              </div>
            ) : null}
          </article>

          <article className="echat-panel">
            <div className="market-import-section-header">
              <h2 className="echat-panel__subtitle">Предпросмотр матчинга</h2>
              <div className="market-import-section-header__actions">
                <TableColumnSettings prefs={columnPrefs} />
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost echat-btn--sm"
                  onClick={scrollToColumnMapping}
                >
                  Изменить столбцы
                </button>
              </div>
            </div>
            <p className="echat-muted">
              Страница {previewPage + 1} из {previewTotalPages} · до {PREVIEW_PAGE_SIZE} строк на
              странице · всего {preview.totalRows}. Иконка карандаша правит поля одной позиции или
              категорию. Чтобы переназначить столбцы файла — «Изменить столбцы». Исключённые строки
              не попадут в импорт.
              {excludedRowNumbers.size > 0 ? ` Исключено: ${excludedRowNumbers.size}.` : ''}
              {highlightMatchingChanges
                ? ' Сначала позиции с правками — листать весь прайс заново не нужно.'
                : ''}
            </p>
            {previewStale ? (
              <div className="market-import-stale-hint" role="status">
                <p className="echat-muted">
                  Сопоставление столбцов изменено — предпросмотр устарел. Обновите его перед импортом.
                </p>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary echat-btn--sm"
                  disabled={busy}
                  onClick={() => void refreshPreviewRows()}
                >
                  Обновить предпросмотр
                </button>
              </div>
            ) : null}
            {loading && loadingMessage === 'Обновляем предпросмотр…' && visibleRows.length === 0 ? (
              <div className="market-import-preview-skeleton" aria-hidden="true">
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
                <div className="market-import-preview-skeleton__row" />
              </div>
            ) : null}
            {visibleRows.length > 0 ? (
              <div
                ref={previewTableRef}
                className={`market-table-wrap${busy ? ' market-table-wrap--loading' : ''}`}
                aria-busy={busy}
              >
                {busy ? (
                  <div className="market-table-wrap__overlay">
                    <MarketLoadingIndicator
                      compact
                      message={loadingMessage ?? 'Загрузка предпросмотра…'}
                    />
                  </div>
                ) : null}
                <table className="market-table market-table--expandable market-table--configurable">
                  <colgroup>
                    {columnPrefs.visibleColumns.map((column) => (
                      <col
                        key={column.id}
                        style={
                          columnPrefs.getWidth(column.id) != null
                            ? { width: columnPrefs.getWidth(column.id) }
                            : undefined
                        }
                      />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      {columnPrefs.visibleColumns.map((column) => (
                        <ResizableTh
                          key={column.id}
                          columnId={column.id}
                          label={column.label}
                          width={columnPrefs.getWidth(column.id)}
                          minWidth={columnPrefs.getMinWidth(column.id)}
                          onResize={columnPrefs.setWidth}
                        >
                          {column.id === 'actions' ? null : column.label}
                        </ResizableTh>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => {
                      const fieldOverride = rowFieldOverrides[row.rowNumber]
                      const displayName = fieldOverride?.name ?? row.name
                      const displayBrand = fieldOverride?.brand ?? row.brand
                      const displayCountry = fieldOverride?.country ?? row.country
                      const displayPrice = fieldOverride?.price ?? row.price
                      const expanded = expandedRow === row.rowNumber
                      const selectedTypeId = effectiveImportRowTypeId(row, rowTypeOverrides)
                      const excluded = excludedRowNumbers.has(row.rowNumber)
                      const stripeClass = index % 2 === 0 ? 'market-table__row--odd' : 'market-table__row--even'
                      const matchingChangeClass =
                        highlightMatchingChanges && row.hasMatchingChange
                          ? 'market-table__row--matching-change'
                          : undefined

                      return (
                        <Fragment key={row.rowNumber}>
                          <tr
                            className={[
                              stripeClass,
                              expanded ? 'market-table__row--expanded' : undefined,
                              excluded ? 'market-table__row--excluded' : undefined,
                              matchingChangeClass,
                            ]
                              .filter(Boolean)
                              .join(' ') || undefined}
                            title={
                              highlightMatchingChanges && row.hasMatchingChange
                                ? 'Есть правки кроме цены'
                                : undefined
                            }
                          >
                            {columnPrefs.visibleColumns.map((column) => {
                              switch (column.id) {
                                case 'row':
                                  return <td key={column.id}>{row.rowNumber}</td>
                                case 'name':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.name ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayName}
                                    </td>
                                  )
                                case 'price':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.price ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayPrice ?? '—'}
                                    </td>
                                  )
                                case 'unit':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.unit ? 'market-table__cell--edited' : undefined}
                                    >
                                      {(fieldOverride?.unit ?? row.unit) ?? '—'}
                                    </td>
                                  )
                                case 'brand':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.brand ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayBrand ?? '—'}
                                    </td>
                                  )
                                case 'country':
                                  return (
                                    <td
                                      key={column.id}
                                      className={fieldOverride?.country ? 'market-table__cell--edited' : undefined}
                                    >
                                      {displayCountry ?? '—'}
                                    </td>
                                  )
                                case 'type':
                                  return (
                                    <td key={column.id}>
                                      {importRowTypeLabel(row, rowTypeOverrides, typeNames)}
                                    </td>
                                  )
                                case 'status':
                                  return (
                                    <td key={column.id}>
                                      {importMatchDecisionLabel(row.matchPreview.decision)}
                                    </td>
                                  )
                                case 'actions':
                                  return (
                                    <td key={column.id}>
                                      <div className="market-table__row-actions">
                                        <button
                                          type="button"
                                          className={`echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn${excluded ? '' : ' market-table__icon-btn--danger'}`}
                                          aria-label={excluded ? `Вернуть строку ${row.rowNumber}` : `Исключить строку ${row.rowNumber}`}
                                          title={excluded ? 'Вернуть' : 'Исключить'}
                                          disabled={busy}
                                          onClick={() => handleToggleExcludedRow(row.rowNumber)}
                                        >
                                          {excluded ? <IconUndo /> : <IconTrash />}
                                        </button>
                                        <button
                                          type="button"
                                          className="echat-btn echat-btn--ghost echat-btn--sm market-table__icon-btn"
                                          aria-label={
                                            expanded
                                              ? `Свернуть строку ${row.rowNumber}`
                                              : `Изменить строку ${row.rowNumber}`
                                          }
                                          title={expanded ? 'Свернуть' : 'Изменить'}
                                          disabled={busy}
                                          onClick={() => setExpandedRow(expanded ? null : row.rowNumber)}
                                        >
                                          <IconPencil />
                                        </button>
                                      </div>
                                    </td>
                                  )
                                default:
                                  return null
                              }
                            })}
                          </tr>
                          {expanded ? (
                            <tr className="market-table__edit-row">
                              <td colSpan={columnPrefs.visibleCount}>
                                <ImportRowEditPanel
                                  row={row}
                                  selectedProductTypeId={selectedTypeId}
                                  fieldOverride={fieldOverride ?? null}
                                  productTypes={productTypes}
                                  brandOptions={brandOptions}
                                  countryOptions={countryOptions}
                                  onTypeChange={(typeId) => {
                                    // Persist only explicit picks (including top candidate).
                                    // Confirm sends these as overrides; unedited rows are not
                                    // pinned from preview so reimport can preserve existing types.
                                    handleRowTypeOverride(row.rowNumber, typeId)
                                  }}
                                  onFieldOverrideChange={(override) =>
                                    handleRowFieldOverride(row.rowNumber, override)
                                  }
                                />
                              </td>
                            </tr>
                          ) : null}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <MarketEmptyState
                title="Нет строк для предпросмотра"
                description="Укажите столбец «Наименование» в маппинге и обновите предпросмотр."
              />
            )}
            {preview.totalRows > PREVIEW_PAGE_SIZE ? (
              <div className="market-pagination">
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy || previewStale || previewPage <= 0}
                  onClick={() => void handlePreviewPageChange(previewPage - 1)}
                >
                  ←
                </button>
                <span>
                  Страница {previewPage + 1} из {previewTotalPages}
                </span>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={busy || previewStale || previewPage >= previewTotalPages - 1}
                  onClick={() => void handlePreviewPageChange(previewPage + 1)}
                >
                  →
                </button>
              </div>
            ) : null}
          </article>

          <div className="market-inline-actions">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={busy || previewStale || isPreviewSessionExpired(preview)}
              onClick={() => {
                if (isRestaurantMyPricesImport) {
                  openSupplierConfirm('confirm-import')
                  return
                }
                void handleConfirm()
              }}
            >
              {excludedRowNumbers.size > 0
                ? `Импортировать (${preview.totalRows - excludedRowNumbers.size} из ${preview.totalRows})`
                : 'Импортировать в каталог'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy || savingDraft || previewStale || isPreviewSessionExpired(preview)}
              onClick={() => void handleSaveDraft()}
            >
              {savingDraft ? 'Сохраняем…' : 'Сохранить черновик'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={busy}
              onClick={() => fileInputRef.current?.click()}
            >
              Другой файл
            </button>
            {draftSavedAt ? (
              <span className="echat-muted">Сохранено {formatDraftExpiry(draftSavedAt)}</span>
            ) : null}
          </div>
        </>
      ) : null}

      <ImportSupplierConfirmModal
        open={supplierConfirmAction != null}
        targetLabel={resolveImportSupplierLabel()}
        privateSuppliers={privateSuppliers}
        currentPrivateSupplierId={supplierMode === 'existing' ? selectedPrivateSupplierId : null}
        onDismiss={() => setSupplierConfirmAction(null)}
        onConfirm={handleSupplierConfirmAccepted}
      />
      <ImportMatchingChangesModal
        open={matchingChangesModalOpen}
        changeCount={matchingChangeCount}
        onPriceOnly={() => {
          setExistingItemUpdateMode('price_only')
          setMatchingChangesModalOpen(false)
          void handleConfirm(undefined, 'price_only')
        }}
        onAcceptAll={() => {
          setExistingItemUpdateMode('accept_match')
          setMatchingChangesModalOpen(false)
          void handleConfirm(undefined, 'accept_match')
        }}
        onReview={() => {
          void handleReviewMatchingChanges()
        }}
        onDismiss={() => setMatchingChangesModalOpen(false)}
      />
    </section>
  )
}
